/**
 * SEODashboard.jsx — SEO Command Center
 * Analytics, insights, health score, breakdown metrics
 */
import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import {
  TrendingUp, AlertTriangle, CheckCircle2, Zap, BarChart2,
  Sparkles, Target, Clock, ArrowUpRight, Loader2, RefreshCw,
  Search, FileText, Image, Star, ShieldCheck, Info
} from "lucide-react";

/* ─────────────── Score Ring ─────────────── */
function BigScoreRing({ score, label }) {
  const r     = 52;
  const circ  = 2 * Math.PI * r;
  const fill  = score == null ? 0 : (score / 100) * circ;
  const color = score == null ? "#94a3b8"
    : score >= 80 ? "#22c55e"
    : score >= 60 ? "#3b82f6"
    : score >= 40 ? "#f59e0b"
    : "#ef4444";
  const statusColors = {
    "ممتاز": "text-green-600 bg-green-50 border-green-200",
    "جيد":   "text-blue-600 bg-blue-50 border-blue-200",
    "ضعيف":  "text-amber-600 bg-amber-50 border-amber-200",
    "حرج":   "text-red-600 bg-red-50 border-red-200",
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <svg width="140" height="140" viewBox="0 0 120 120">
        {/* glow */}
        <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f5f9" strokeWidth="10" />
        <circle
          cx="60" cy="60" r={r} fill="none"
          stroke={color} strokeWidth="10"
          strokeDasharray={`${fill} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 60 60)"
          style={{ transition: "stroke-dasharray 1.2s cubic-bezier(0.34,1.56,0.64,1)" }}
          filter={`drop-shadow(0 0 6px ${color}55)`}
        />
        <text x="60" y="55" textAnchor="middle" fontSize="26" fontWeight="900" fill={color}>
          {score ?? "—"}
        </text>
        <text x="60" y="73" textAnchor="middle" fontSize="11" fill="#94a3b8">
          / 100
        </text>
      </svg>
      {label && (
        <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusColors[label] || ""}`}>
          {label}
        </span>
      )}
    </div>
  );
}

/* ─────────────── Metric Bar ─────────────── */
function MetricBar({ label, score, icon: Icon, color }) {
  const colors = {
    green: { bar: "bg-green-500",  text: "text-green-600",  bg: "bg-green-50"  },
    blue:  { bar: "bg-blue-500",   text: "text-blue-600",   bg: "bg-blue-50"   },
    amber: { bar: "bg-amber-500",  text: "text-amber-600",  bg: "bg-amber-50"  },
    red:   { bar: "bg-red-500",    text: "text-red-600",    bg: "bg-red-50"    },
  };
  const c = colors[color] || colors.blue;

  return (
    <div className="flex items-center gap-3 group">
      <div className={`w-8 h-8 rounded-xl ${c.bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-4 h-4 ${c.text}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-semibold text-slate-600">{label}</span>
          <span className={`text-xs font-black ${c.text}`}>{score}%</span>
        </div>
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${c.bar} rounded-full transition-all duration-1000 ease-out`}
            style={{ width: `${score}%` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─────────────── Insight Card ─────────────── */
function InsightCard({ insight }) {
  const map = {
    opportunity: { border: "border-blue-200",   bg: "bg-blue-50",   icon: <Sparkles className="w-4 h-4 text-blue-500" />,   title: "text-blue-700"  },
    warning:     { border: "border-amber-200",  bg: "bg-amber-50",  icon: <AlertTriangle className="w-4 h-4 text-amber-500" />, title: "text-amber-700" },
    critical:    { border: "border-red-200",    bg: "bg-red-50",    icon: <AlertTriangle className="w-4 h-4 text-red-500" />,   title: "text-red-700"   },
    success:     { border: "border-green-200",  bg: "bg-green-50",  icon: <CheckCircle2 className="w-4 h-4 text-green-500" />,  title: "text-green-700" },
  };
  const s = map[insight.type] || map.opportunity;

  return (
    <div className={`border ${s.border} ${s.bg} rounded-2xl p-3 flex gap-3`}>
      <div className="shrink-0 mt-0.5">{s.icon}</div>
      <div>
        <p className={`text-xs font-bold ${s.title}`}>{insight.title}</p>
        <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{insight.text}</p>
      </div>
    </div>
  );
}

/* ─────────────── Product Row ─────────────── */
function ProductRow({ product, type }) {
  const issueCount = product.issues?.length || 0;
  return (
    <div className="flex items-center gap-2 py-2 border-b border-slate-50 last:border-0">
      {type === "strong" && <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />}
      {type === "urgent" && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />}
      {type === "weak"   && <Info className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
      <span className="text-xs text-slate-700 flex-1 truncate">{product.product_name}</span>
      {type !== "strong" && (
        <span className="text-[10px] font-bold text-slate-400">{issueCount} مشكلة</span>
      )}
    </div>
  );
}

/* ─────────────── Timeline Item ─────────────── */
function TimelineItem({ item }) {
  const typeLabel = { title: "عنوان", description: "وصف" };
  const statusColors = {
    applied:  "bg-green-100 text-green-700",
    approved: "bg-blue-100 text-blue-700",
    pending:  "bg-slate-100 text-slate-500",
    rejected: "bg-red-100 text-red-500",
  };
  const statusLabels = { applied: "مُطبَّق", approved: "معتمد", pending: "معلّق", rejected: "مرفوض" };
  const date = item.applied_at || item.created_at;
  const ago  = date ? new Date(date).toLocaleDateString("ar-SA", { month: "short", day: "numeric" }) : "";

  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-50 last:border-0">
      <div className="w-1.5 h-1.5 rounded-full bg-[#004EEB] mt-1.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-700 truncate">
          {typeLabel[item.type] || item.type} — <span className="font-medium">{item.product_name}</span>
        </p>
        <p className="text-[10px] text-slate-400">{ago}</p>
      </div>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${statusColors[item.status] || ""}`}>
        {statusLabels[item.status] || item.status}
      </span>
    </div>
  );
}

/* ══════════════════════ SEODashboard ══════════════════════ */
export default function SEODashboard({ onGoToAudit }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/seo/analytics");
      setData(d);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-300" />
      </div>
    );
  }

  if (!data?.has_data) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center">
          <BarChart2 className="w-8 h-8 text-slate-300" />
        </div>
        <p className="text-sm font-bold text-slate-700">لا توجد بيانات تحليلية بعد</p>
        <p className="text-xs text-slate-400 max-w-xs">ابدأ بفحص متجرك للحصول على لوحة تحليلات SEO كاملة</p>
        <button onClick={onGoToAudit}
          className="flex items-center gap-2 bg-[#004EEB] text-white text-sm font-bold px-5 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
          <Search className="w-4 h-4" /> ابدأ الفحص الآن
        </button>
      </div>
    );
  }

  const bd = data.breakdown || {};
  const gm = data.growth_metrics || {};

  const breakdownItems = [
    { key: "title_quality",        label: "جودة العناوين",       icon: FileText },
    { key: "description_quality",  label: "جودة الأوصاف",        icon: FileText },
    { key: "image_coverage",       label: "تغطية الصور",          icon: Image    },
    { key: "keyword_optimization", label: "تحسين الكلمات المفتاحية", icon: Search },
    { key: "readability",          label: "سهولة القراءة",        icon: Star     },
    { key: "content_completeness", label: "اكتمال المحتوى",       icon: ShieldCheck },
  ];

  return (
    <div className="space-y-5">

      {/* ── Row 1: Health Score + Breakdown ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">

        {/* Health Score */}
        <div className="lg:col-span-2 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm flex flex-col items-center justify-center gap-4">
          <div className="flex items-center justify-between w-full">
            <div>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">نقاط صحة SEO</p>
              <p className="text-xs text-slate-500 mt-0.5">تحليل شامل للمتجر</p>
            </div>
            <button onClick={load}
              className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
          <BigScoreRing score={data.health_score} label={data.health_label} />

          {/* Benchmark */}
          <div className="w-full bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-xl px-3 py-2 text-center">
            <p className="text-[11px] text-slate-500">
              متجرك أقوى من{" "}
              <span className="font-black text-[#004EEB]">{data.benchmark_percentile}%</span>
              {" "}من المتاجر المماثلة
            </p>
          </div>
        </div>

        {/* Breakdown */}
        <div className="lg:col-span-3 bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide mb-4">تحليل مكونات SEO</p>
          <div className="space-y-3.5">
            {breakdownItems.map((item) => {
              const d = bd[item.key];
              if (!d) return null;
              return (
                <MetricBar
                  key={item.key}
                  label={item.label}
                  score={d.score}
                  icon={item.icon}
                  color={d.color}
                />
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Row 2: Growth Metrics ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          {
            label: "فرصة زيادة الظهور",
            value: `+${gm.visibility_potential}%`,
            sub: "إمكانية التحسين",
            icon: <TrendingUp className="w-5 h-5 text-blue-500" />,
            bg: "bg-blue-50",
          },
          {
            label: "فرص تحسين متبقية",
            value: gm.opportunities_remaining,
            sub: "منتج يحتاج تحسيناً",
            icon: <Target className="w-5 h-5 text-amber-500" />,
            bg: "bg-amber-50",
          },
          {
            label: "المنتجات المُحسَّنة",
            value: `${gm.optimized_percentage}%`,
            sub: `${data.applied_suggestions} من ${data.total_suggestions} اقتراح`,
            icon: <Zap className="w-5 h-5 text-green-500" />,
            bg: "bg-green-50",
          },
          {
            label: "اقتراحات AI معلّقة",
            value: data.pending_suggestions,
            sub: "بانتظار مراجعتك",
            icon: <Sparkles className="w-5 h-5 text-purple-500" />,
            bg: "bg-purple-50",
          },
        ].map((m, i) => (
          <div key={i} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
            <div className={`w-9 h-9 rounded-xl ${m.bg} flex items-center justify-center mb-3`}>
              {m.icon}
            </div>
            <p className="text-2xl font-black text-slate-800">{m.value}</p>
            <p className="text-xs font-semibold text-slate-600 mt-0.5">{m.label}</p>
            <p className="text-[10px] text-slate-400 mt-0.5">{m.sub}</p>
          </div>
        ))}
      </div>

      {/* ── Row 3: AI Insights ── */}
      {data.insights?.length > 0 && (
        <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-xl bg-gradient-to-br from-[#004EEB] to-purple-500 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wide">رؤى AI الذكية</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.insights.map((ins, i) => (
              <InsightCard key={i} insight={ins} />
            ))}
          </div>
        </div>
      )}

      {/* ── Row 4: Products Analysis + Timeline ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Urgent */}
        <div className="bg-white border border-red-100 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <p className="text-xs font-bold text-slate-700">تحتاج تدخل عاجل</p>
          </div>
          {data.urgent_products?.length === 0
            ? <p className="text-xs text-slate-400 py-4 text-center">لا توجد منتجات حرجة</p>
            : data.urgent_products?.map((p, i) => <ProductRow key={i} product={p} type="urgent" />)
          }
        </div>

        {/* Weak */}
        <div className="bg-white border border-amber-100 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-4 h-4 text-amber-400" />
            <p className="text-xs font-bold text-slate-700">الأضعف في SEO</p>
          </div>
          {data.weak_products?.length === 0
            ? <p className="text-xs text-slate-400 py-4 text-center">لا توجد منتجات ضعيفة</p>
            : data.weak_products?.map((p, i) => <ProductRow key={i} product={p} type="weak" />)
          }
        </div>

        {/* Timeline */}
        <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-slate-400" />
            <p className="text-xs font-bold text-slate-700">سجل نشاط SEO</p>
          </div>
          {data.timeline?.length === 0
            ? <p className="text-xs text-slate-400 py-4 text-center">لا يوجد نشاط بعد</p>
            : data.timeline?.map((item, i) => <TimelineItem key={i} item={item} />)
          }
        </div>
      </div>

      {/* ── CTA ── */}
      {gm.opportunities_remaining > 0 && (
        <div className="bg-gradient-to-l from-[#004EEB]/10 to-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-bold text-slate-800">
              {gm.opportunities_remaining} منتج يحتاج تحسين SEO
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              التحسين يرفع ظهورك في محركات البحث بنسبة تصل لـ +{gm.visibility_potential}%
            </p>
          </div>
          <button onClick={onGoToAudit}
            className="shrink-0 flex items-center gap-2 bg-[#004EEB] text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-blue-700 transition-colors">
            <ArrowUpRight className="w-4 h-4" /> ابدأ التحسين
          </button>
        </div>
      )}
    </div>
  );
}
